import { inject, Injectable } from '@angular/core';
import { RestApiService } from '@core/services/rest-api.service';
import { MapInput } from '@features/map/interfaces';
import { CustomerRouteNetwork } from '@pages/route-network-page/interfaces';
import { RouteNetworkLayerBuilderService } from '@pages/route-network-page/services/route-network-layer-builder.service';
import { Entity } from '@shared/constants/entity';
import { RouteNetwork } from '@shared/interfaces/route-network';
import { map, Observable, take, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ClientRouteNetworkStoreService {
  private layerBuilderService = inject(RouteNetworkLayerBuilderService);
  private api = inject(RestApiService);

  private customerRouteNetworks: CustomerRouteNetwork[] = [];

  private changedRouteNetworkId: number | undefined;

  triggerRouteNetworkRefetchIfNeeded(): void {
    const changedNetworkId = this.changedRouteNetworkId;
    if (changedNetworkId) {
      this.getRouteSegments(changedNetworkId, true)
        .pipe(
          take(1),
          tap(resp =>
            this.updateCustomerRouteNetwork({
              routeNetworkId: changedNetworkId,
              mapInput: resp,
            })
          )
        )
        .subscribe();
    }
  }

  getCustomerRouteNetworkCount(): number {
    return this.customerRouteNetworks.length;
  }

  registerRouteNetworkChange(id: number): void {
    this.changedRouteNetworkId = id;
  }

  clearRouteNetworkStore(): void {
    this.changedRouteNetworkId = undefined;
    this.customerRouteNetworks = [];
  }

  getRouteSegments(
    entityId: number,
    enableHoverAnimation = false,
    enableEdit = false,
    extendedEditFn?: (event: L.LeafletMouseEvent) => void,
    getAllRoutes = false
  ): Observable<MapInput[]> {
    const requestFields = ['RouteSegments', 'RouteNetworkID'];

    const shouldAllRoutesBeFetched =
      getAllRoutes && !this.getCustomerRouteNetworkCount();

    const entityIdNumber = Number(entityId);

    return this.getRouteNetworkData(
      requestFields,
      shouldAllRoutesBeFetched ? undefined : entityId
    ).pipe(
      tap(data => {
        if (shouldAllRoutesBeFetched) {
          this.preserveCustomerRouteNetworks(data, enableHoverAnimation);
        }
      }),
      map(routeNetworks => {
        const currentlySelectedRouteNetwork = routeNetworks.find(
          routeNetwork => routeNetwork.RouteNetworkID === entityIdNumber
        );

        if (!currentlySelectedRouteNetwork) return [];

        return [
          ...(getAllRoutes
            ? this.getNonEditableRouteNetworkMapData(entityIdNumber)
            : []),
          ...this.layerBuilderService.mapRouteNetworkToMapMarkerInput(
            [currentlySelectedRouteNetwork],
            enableEdit,
            extendedEditFn,
            enableHoverAnimation
          ),
        ];
      })
    );
  }

  private preserveCustomerRouteNetworks(
    routeNetworks: RouteNetwork[],
    enableHoverAnimation: boolean
  ): void {
    /** saving networks as non editable that fetch would not repeat */
    routeNetworks.forEach(item => {
      const nonEditableInput =
        this.layerBuilderService.mapRouteNetworkToMapMarkerInput(
          [item],
          false,
          undefined,
          enableHoverAnimation
        );

      const index = this.customerRouteNetworks.findIndex(
        x => x.routeNetworkId === item.RouteNetworkID
      );

      const customerRouteNetwork = {
        routeNetworkId: item.RouteNetworkID!,
        mapInput: nonEditableInput,
      };
      if (index >= 0) {
        this.updateCustomerRouteNetwork(customerRouteNetwork);
      } else {
        this.customerRouteNetworks.push(customerRouteNetwork);
      }
    });
  }

  private getNonEditableRouteNetworkMapData(entityId: number): MapInput[] {
    return this.customerRouteNetworks
      .filter(routeNetwork => routeNetwork.routeNetworkId !== entityId)
      .map(item => item.mapInput)
      .flat();
  }

  private getRouteNetworkData(
    requestFields: string[],
    entityId?: number
  ): Observable<RouteNetwork[]> {
    return this.api
      .getData<RouteNetwork>({
        entity: Entity.RouteNetworkWeb,
        fields: requestFields,
        ...(entityId
          ? {
              filterModel: {
                RouteNetworkID: {
                  filterType: 'text',
                  type: 'equals',
                  filter: entityId,
                },
              },
            }
          : {}),
      })
      .pipe(map(resp => resp.data));
  }

  private updateCustomerRouteNetwork(newItem: CustomerRouteNetwork): void {
    this.customerRouteNetworks = this.customerRouteNetworks.map(
      routeNetwork => {
        if (routeNetwork.routeNetworkId === newItem.routeNetworkId) {
          return newItem;
        }

        return routeNetwork;
      }
    );
  }
}
